import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { squidloansUserAPI } from '../../services/api';
import { LEAD_STAGE, automationNodeLeadStageToRouteMap } from '../../Utils/constants';
import useLeadStage from '../../hooks/useLeadStage';
import Toast from '../../components/Toast';
import RepaymentCalendar from './RepaymentCalendar';
import PageLoader from '../../components/Loader';
import FlowFooter from '../../components/FlowFooter';

const curr_page_lead_stage = [
  LEAD_STAGE.BRE_APPROVED,
  LEAD_STAGE.MANUALLY_APPROVE,
  LEAD_STAGE.VERIFY_EMAIL_OTP,
];

function LoanAmount() {
  const navigate = useNavigate();
  const {
    leadStage,
    isLoadingStage,
    isReloan,
    hasAppliedCoupon,
    discountReceived,
    appliedCouponCodeBackend,
  } = useLeadStage();

  const [maxAmount, setMaxAmount] = useState(15000);
  const [amount, setAmount] = useState('');
  const [loanAmountRequested, setLoanAmountRequested] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRepaymentCalendar, setShowRepaymentCalendar] = useState(false);
  const [giftType, setGiftType] = useState(() => localStorage.getItem('gift_type') || '');

  // Coupon state
  const [showCouponField, setShowCouponField] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponVerificationStatus, setCouponVerificationStatus] = useState({
    isValid: null,
    message: '',
  });
  const [couponDebounceTimer, setCouponDebounceTimer] = useState(null);
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [canChangeCoupon, setCanChangeCoupon] = useState(false);

  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

  // Lead stage redirect
  useEffect(() => {
    if (isLoadingStage) return;
    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
    }
  }, [leadStage, navigate, isLoadingStage]);

  // Sync coupon from backend
  useEffect(() => {
    setAppliedCouponCode(appliedCouponCodeBackend);
    setAppliedDiscount(discountReceived);
  }, [appliedCouponCodeBackend, discountReceived]);

  // Cleanup coupon debounce
  useEffect(() => {
    return () => {
      if (couponDebounceTimer) clearTimeout(couponDebounceTimer);
    };
  }, [couponDebounceTimer]);

  // Fetch max loan amount and auto-fill coupon from leadCoupons
  useEffect(() => {
    const fetchMaxAmount = async () => {
      try {
        const resp = await squidloansUserAPI.getRequestedLoanAmount();
        const cap = Math.min(resp?.max_loan_amount || 0, 100000) || 15000;
        setMaxAmount(cap);
        if (cap) {
          setAmount(String(cap));
          setLoanAmountRequested(cap);
        }
        // Auto-fill coupon if leadCoupons present
        const leadCoupons = resp?.leadCoupons;
        if (Array.isArray(leadCoupons) && leadCoupons.length > 0) {
          const firstCoupon = leadCoupons.find((lc) => lc?.coupon) || leadCoupons[0];
          const coupon = firstCoupon?.coupon;
          const code = coupon?.coupon_code;
          if (code) {
            setCouponCode(code);
            if (firstCoupon?.is_applied) {
              setAppliedCouponCode(code);
              setAppliedDiscount(Number(coupon?.discount_percent) || 0);
              setIsCouponApplied(true);
            } else {
              setShowCouponField(true);
              setCouponVerificationStatus({
                isValid: true,
                message: 'Click on Apply to redeem.',
              });
            }
          }
        }
      } catch (e) {
        setToast({
          show: true,
          message: e?.message || 'Failed to fetch maximum loan amount',
          type: 'error',
        });
      }
    };
    fetchMaxAmount();
  }, []);

  const showToast = (message, type = 'warning') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', type: 'error' });
  };

  const handleCouponChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCouponCode(value);
    setCouponVerificationStatus({ isValid: null, message: '' });

    if (couponDebounceTimer) clearTimeout(couponDebounceTimer);

    if (value.length >= 6) {
      const timer = setTimeout(async () => {
        try {
          const response = await squidloansUserAPI.verifyCouponCode(value);
          setCouponVerificationStatus({
            isValid: true,
            message: `Valid coupon! Click APPLY to get ${response?.discount ?? 0}% discount.`,
          });
        } catch (err) {
          setCouponVerificationStatus({
            isValid: false,
            message: err?.message || 'Invalid coupon',
          });
        }
      }, 500);
      setCouponDebounceTimer(timer);
    } else if (value.length > 0 && value.length < 6) {
      setCouponVerificationStatus({
        isValid: null,
        message: 'Coupon code must be at least 6 characters',
      });
    } else {
      setCouponVerificationStatus({ isValid: null, message: '' });
    }
  };

  const handleApplyCoupon = async () => {
    if (couponVerificationStatus.isValid !== true) {
      showToast('Please enter a valid coupon code first', 'warning');
      return;
    }
    try {
      const response = await squidloansUserAPI.applyCouponCode(couponCode);
      if (response?.success !== false) {
        setAppliedDiscount(response?.discount ?? 0);
        setIsCouponApplied(true);
        setAppliedCouponCode(couponCode);
        setCanChangeCoupon(false);
        showToast('Coupon applied successfully!', 'success');
      } else {
        showToast(response?.message || 'Failed to apply coupon', 'error');
      }
    } catch (err) {
      showToast(err?.message || 'Failed to apply coupon', 'error');
    }
  };

  const handleChangeCoupon = () => {
    setIsCouponApplied(false);
    setAppliedCouponCode('');
    setCouponCode('');
    setCouponVerificationStatus({ isValid: null, message: '' });
    setAppliedDiscount(0);
    setCanChangeCoupon(true);
    setShowCouponField(true);
  };

  const handleAmountSelect = (val) => {
    setLoanAmountRequested(val);
    setAmount(String(val));
  };

  const handleManualAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setAmount(value);
    if (value) {
      const num = parseInt(value, 10);
      if (num >= 5000 && num <= maxAmount) {
        setLoanAmountRequested(num);
      }
    }
  };

  const handleGiftTypeChange = (value) => {
    setGiftType(value);
    localStorage.setItem('gift_type', value);
  };

  const generateAmountOptions = () => {
    const options = [];
    const minAmount = 5000;
    const step = 5000;
    for (let a = minAmount; a <= maxAmount; a += step) {
      options.push(a);
    }
    return options;
  };

  const handleProceedToCalendar = (e) => {
    e.preventDefault();
    setError('');
    const numeric = parseInt(amount || '0', 10);
    if (!numeric || numeric < 5000 || (maxAmount && numeric > maxAmount)) {
      setError(
        maxAmount
          ? `Amount must be between ₹5,000 and ₹${maxAmount.toLocaleString()}`
          : 'Please enter a valid amount above ₹5,000'
      );
      return;
    }
    if (!giftType) {
      setError('Please select Gold or Silver');
      return;
    }
    localStorage.setItem('gift_type', giftType);
    setLoanAmountRequested(numeric);
    setShowRepaymentCalendar(true);
  };

  const handleRequestLoan = async () => {
    const numeric = loanAmountRequested || parseInt(amount || '0', 10);
    if (!numeric || numeric < 5000 || numeric > maxAmount) {
      showToast(`Please enter an amount between ₹5,000 - ₹${maxAmount.toLocaleString()}`, 'error');
      return;
    }

    const selectedRepaymentDate = localStorage.getItem('selectedRepaymentDate');
    const selectedTenure = localStorage.getItem('selectedTenure');
    const storedGiftType = localStorage.getItem('gift_type') || giftType;
    if (!selectedRepaymentDate || !selectedTenure) {
      showToast('Please select a repayment date and tenure', 'error');
      return;
    }
    if (!storedGiftType) {
      showToast('Please select Gold or Silver', 'error');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await squidloansUserAPI.requestLoan({
        loan_amount: numeric,
        repaymentDate: selectedRepaymentDate,
        tenure: parseInt(selectedTenure, 10),
        gift_type: storedGiftType,
      });

      if (isReloan) {
        navigate('/apply/e-sign');
        return;
      }

      const kycResp = await squidloansUserAPI.initiateKYC({});
      const redirectUrl = kycResp?.redirectUrl || kycResp?.redirect_url;
      if (kycResp?.success && redirectUrl) {
        if (kycResp?.unifiedTransactionId) {
          localStorage.setItem('unifiedTransactionId', kycResp.unifiedTransactionId);
        }
        window.location.href = redirectUrl;
      } else {
        navigate('/apply/pre-e-sign');
      }
    } catch (e) {
      showToast(e?.message || 'Failed to request loan', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!leadStage && isLoadingStage) {
    return <PageLoader />;
  }

  if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
    return null;
  }

  if (showRepaymentCalendar) {
    return (
      <RepaymentCalendar
        onBack={() => setShowRepaymentCalendar(false)}
        loanAmount={loanAmountRequested}
        onRequestLoan={handleRequestLoan}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-md w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#1E3A8A]">
              Choose your <span className="text-[#FF6B35]">loan amount</span>
            </h1>
            {maxAmount > 0 && (
              <p className="text-sm sm:text-base text-[#64748B]">
                You are eligible for up to{' '}
                <span className="font-semibold text-[#1E3A8A]">₹{maxAmount.toLocaleString()}</span>
              </p>
            )}
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <form onSubmit={handleProceedToCalendar} className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1">
                  Select Loan Amount <span className="text-[#FF6B35]">*</span>
                </label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {generateAmountOptions().map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleAmountSelect(opt)}
                      className={`py-2 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                        loanAmountRequested === opt
                          ? 'bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white border-2 border-transparent shadow-md'
                          : 'bg-[#F8FAFC] text-[#1E293B] border border-[#E2E8F0] hover:bg-[#F1F5F9]'
                      }`}
                    >
                      ₹{opt / 1000}k
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min={5000}
                  max={maxAmount}
                  value={amount}
                  onChange={handleManualAmountChange}
                  placeholder="Enter custom amount"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-white border border-[#E2E8F0] text-sm sm:text-base text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 focus:border-[#FF6B35]"
                />
                <p className="text-[10px] sm:text-xs text-[#94A3B8] mt-1">
                  Min ₹5,000 · Max ₹{maxAmount.toLocaleString()}
                </p>
                {error && <p className="text-xs text-red-500 mt-1 sm:mt-2">{error}</p>}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-2">
                  Select Gift Type <span className="text-[#FF6B35]">*</span>
                </label>
                <div className="flex gap-3">
                  {[
                    { value: 'gold', label: 'Gold' },
                    { value: 'silver', label: 'Silver' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 sm:py-3 px-3 rounded-xl sm:rounded-2xl text-sm font-medium cursor-pointer transition-all border ${
                        giftType === option.value
                          ? 'bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white border-transparent shadow-md'
                          : 'bg-[#F8FAFC] text-[#1E293B] border-[#E2E8F0] hover:bg-[#F1F5F9]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="gift_type"
                        value={option.value}
                        checked={giftType === option.value}
                        onChange={() => handleGiftTypeChange(option.value)}
                        className="sr-only"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Coupon */}
              <div>
                {(!isCouponApplied && !hasAppliedCoupon) || canChangeCoupon ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowCouponField(!showCouponField)}
                      className="w-full flex items-center justify-between py-2 px-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-xs sm:text-sm text-[#1E293B]"
                    >
                      <span>Have a Coupon Code?</span>
                      <span className="text-[#FF6B35] font-semibold">{showCouponField ? 'Hide' : 'Show'}</span>
                    </button>
                    {showCouponField && (
                      <div className="mt-2 space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={handleCouponChange}
                            placeholder="Enter coupon"
                            className={`flex-1 px-3 py-2 rounded-xl bg-white border text-xs sm:text-sm text-[#1E293B] placeholder:text-[#94A3B8] ${
                              couponVerificationStatus.isValid === true
                                ? 'border-green-500'
                                : couponVerificationStatus.isValid === false
                                ? 'border-red-500'
                                : 'border-[#E2E8F0]'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={handleApplyCoupon}
                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-xs sm:text-sm font-medium shadow-md"
                          >
                            Apply
                          </button>
                        </div>
                        {couponVerificationStatus.message && (
                          <p
                            className={`text-xs ${
                              couponVerificationStatus.isValid === true
                                ? 'text-green-600'
                                : couponVerificationStatus.isValid === false
                                ? 'text-red-500'
                                : 'text-[#64748B]'
                            }`}
                          >
                            {couponVerificationStatus.message}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-green-50 border border-green-200">
                    <span className="text-xs sm:text-sm text-green-700">
                      {appliedCouponCode || appliedCouponCodeBackend} applied ({appliedDiscount}% off on PF)
                    </span>
                    <button
                      type="button"
                      onClick={handleChangeCoupon}
                      className="text-xs bg-white text-[#1E293B] px-3 py-1.5 rounded-lg border border-[#E2E8F0] font-semibold hover:bg-[#F8FAFC]"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={!loanAmountRequested || !giftType || isLoading}
                className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Processing...
                  </span>
                ) : (
                  'Proceed'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <FlowFooter />
    </div>
  );
}

export default LoanAmount;
